import { Link } from "react-router-dom";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import { getBaseUrl, buildBreadcrumbSchema } from "@/lib/seo";

const COOKIES_TITLE = "Política de Cookies | FabChat";
const COOKIES_DESCRIPTION =
  "Política de Cookies da FabChat. Como utilizamos cookies e tecnologias similares na nossa plataforma.";

const breadcrumbItems = [
  { name: "Início", path: "/" },
  { name: "Política de Cookies", path: "/cookies" },
];

export default function CookiesPolicy() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, breadcrumbItems);

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={COOKIES_TITLE}
        description={COOKIES_DESCRIPTION}
        canonicalPath="/cookies"
      />
      <StructuredData data={breadcrumbSchema} />
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center font-bold text-xl">
            <img src="/logo-light.png?v=6" alt="FabChat Logo" className="h-10 w-auto object-contain block dark:hidden" />
            <img src="/logo.png?v=6" alt="FabChat Logo" className="h-10 w-auto object-contain hidden dark:block" />
          </Link>
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Voltar ao início
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-4">Política de Cookies</h1>
          <p className="text-muted-foreground mb-8">
            <strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-BR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. O que são Cookies?</h2>
            <p className="mb-4">
              Esta Política de Cookies integra os documentos legais da FabChat. Para o uso geral dos nossos serviços, consulte também nossos <Link to="/termos" className="text-primary hover:underline">Termos de Serviço</Link>.
            </p>
            <p className="mb-4">
              Cookies são pequenos arquivos de texto que são armazenados no seu dispositivo (computador, tablet ou celular) quando você visita um site. Eles são amplamente utilizados para fazer os sites funcionarem de forma mais eficiente, bem como para fornecer informações aos proprietários do site.
            </p>
            <p className="mb-4">
              Os cookies podem ser "persistentes" (permanecem no seu dispositivo por um período definido) ou "de sessão" (são deletados quando você fecha o navegador).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Como Usamos Cookies</h2>
            <p className="mb-4">
              A FabChat utiliza cookies e tecnologias similares de forma transparente e em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018) e o Marco Civil da Internet (Lei 12.965/2014). Nossa utilização de cookies segue os princípios de:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Finalidade:</strong> Utilizamos cookies apenas para finalidades específicas e legítimas</li>
              <li><strong>Adequação:</strong> Coletamos apenas dados necessários para as finalidades declaradas</li>
              <li><strong>Necessidade:</strong> Limitamos o uso de cookies ao mínimo necessário</li>
              <li><strong>Transparência:</strong> Informamos claramente sobre o uso de cookies</li>
              <li><strong>Segurança:</strong> Implementamos medidas técnicas para proteger dados coletados via cookies</li>
              <li><strong>Prevenção:</strong> Adotamos medidas para prevenir uso não autorizado de cookies</li>
              <li><strong>Não discriminação:</strong> Não utilizamos cookies de forma discriminatória</li>
              <li><strong>Responsabilização:</strong> Mantemos registros e controles sobre o uso de cookies</li>
            </ul>
            
            <h3 className="text-xl font-semibold mb-3">2.1. Finalidades Específicas</h3>
            <p className="mb-4">Utilizamos cookies exclusivamente para as seguintes finalidades:</p>
            <div className="bg-muted p-4 rounded-lg mb-4">
              <ul className="space-y-3 text-sm">
                <li>
                  <strong>Autenticação e Segurança:</strong> Manter você autenticado quando faz login, prevenir acesso não autorizado e garantir a segurança da sua conta e dados.
                </li>
                <li>
                  <strong>Funcionalidade Essencial:</strong> Permitir que funcionalidades básicas do site funcionem corretamente, como manter sua sessão ativa e lembrar suas preferências.
                </li>
                <li>
                  <strong>Personalização:</strong> Lembrar suas preferências de tema (modo claro/escuro) e outras configurações para melhorar sua experiência.
                </li>
                <li>
                  <strong>Melhoria de Serviços:</strong> Entender como você utiliza nossos serviços para identificar problemas e melhorar funcionalidades (apenas com seu consentimento explícito).
                </li>
                <li>
                  <strong>Conformidade Legal:</strong> Cumprir obrigações legais e regulatórias, incluindo requisitos de segurança e auditoria.
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. Tipos de Cookies que Utilizamos</h2>

            <h3 className="text-xl font-semibold mb-3">3.1. Cookies Estritamente Necessários</h3>
            <p className="mb-4">
              Estes cookies são essenciais para o funcionamento do site e não podem ser desativados em nossos sistemas. Eles geralmente são configurados apenas em resposta a ações feitas por você, como login, definição de preferências de privacidade, preenchimento de formulários, etc.
            </p>
            <div className="bg-muted p-4 rounded-lg mb-4">
              <p className="text-sm font-semibold mb-2">Cookies Essenciais:</p>
              <ul className="text-sm space-y-1">
                <li><strong>sb-access-token:</strong> Token de autenticação do Supabase (Sessão)</li>
                <li><strong>sb-refresh-token:</strong> Token de renovação de sessão do Supabase (Persistente, 30 dias)</li>
                <li><strong>sb-auth-token:</strong> Informações de autenticação (Sessão)</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mb-3">3.2. Cookies Funcionais</h3>
            <p className="mb-4">
              Estes cookies permitem que o site forneça funcionalidades e personalização aprimoradas. Eles podem ser definidos por nós ou por fornecedores terceiros cujos serviços adicionamos às nossas páginas.
            </p>
            <div className="bg-muted p-4 rounded-lg mb-4">
              <p className="text-sm font-semibold mb-2">Cookies Funcionais:</p>
              <ul className="text-sm space-y-1">
                <li><strong>theme:</strong> Armazena sua preferência de tema (dark/light mode) (Persistente, 1 ano)</li>
                <li><strong>cookie-consent:</strong> Armazena sua preferência de cookies (Persistente, 1 ano)</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mb-3">3.3. Cookies de Desempenho e Análise</h3>
            <p className="mb-4">
              Estes cookies nos permitem contar visitas e fontes de tráfego para que possamos medir e melhorar o desempenho do nosso site. Eles nos ajudam a saber quais páginas são mais e menos populares e ver como os visitantes se movem pelo site.
            </p>
            <p className="mb-4 text-sm text-muted-foreground">
              <em>Nota: Atualmente, não utilizamos cookies de análise de terceiros. Se implementarmos no futuro, você será notificado e poderá gerenciar suas preferências.</em>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Cookies de Terceiros</h2>
            <p className="mb-4">
              Alguns cookies são colocados por serviços de terceiros que aparecem em nossas páginas. Não temos controle sobre esses cookies. Estes serviços incluem:
            </p>

            <h3 className="text-xl font-semibold mb-3">4.1. Supabase (Autenticação e Banco de Dados)</h3>
            <p className="mb-4">
              Utilizamos o Supabase para gerenciar autenticação de usuários e armazenamento de dados. O Supabase pode definir cookies para manter você conectado e garantir a segurança da sua sessão.
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Finalidade:</strong> Autenticação e gerenciamento de sessão</li>
              <li><strong>Tipo:</strong> Essencial</li>
              <li><strong>Política de Privacidade:</strong> <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">supabase.com/privacy</a></li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">4.2. Stripe (Processamento de Pagamentos)</h3>
            <p className="mb-4">
              Quando você realiza um pagamento, o Stripe pode definir cookies para processar transações de forma segura e prevenir fraudes.
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Finalidade:</strong> Processamento seguro de pagamentos</li>
              <li><strong>Tipo:</strong> Essencial para transações</li>
              <li><strong>Política de Privacidade:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">stripe.com/privacy</a></li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">4.3. OpenAI (Processamento de IA)</h3>
            <p className="mb-4">
              Utilizamos a API da OpenAI para fornecer funcionalidades de inteligência artificial. A OpenAI não define cookies diretamente no seu navegador, mas processa dados conforme nossa solicitação.
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Finalidade:</strong> Processamento de conversas com IA</li>
              <li><strong>Tipo:</strong> Processamento de dados via API</li>
              <li><strong>Política de Privacidade:</strong> <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">openai.com/privacy</a></li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">4.4. Responsabilidade por Cookies de Terceiros</h3>
            <p className="mb-4">
              <strong>IMPORTANTE:</strong> A FabChat não controla os cookies definidos por serviços de terceiros. Cada serviço de terceiro possui suas próprias políticas de privacidade e uso de cookies. Recomendamos que você revise as políticas de privacidade de cada serviço:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Cookies de terceiros são regidos pelas políticas de privacidade dos respectivos provedores</li>
              <li>A FabChat não se responsabiliza pelo uso de cookies por serviços de terceiros</li>
              <li>Você pode gerenciar preferências de cookies de terceiros através das configurações do seu navegador</li>
              <li>Alguns serviços de terceiros podem não funcionar corretamente se você bloquear seus cookies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Como Gerenciar Cookies</h2>
            
            <h3 className="text-xl font-semibold mb-3">5.1. Configurações do Navegador</h3>
            <p className="mb-4">
              A maioria dos navegadores permite que você controle cookies através das configurações. Você pode configurar seu navegador para recusar todos ou alguns cookies, ou para alertá-lo quando sites definem ou acessam cookies.
            </p>
            <p className="mb-4">
              <strong>Importante:</strong> Se você desabilitar ou recusar cookies, algumas partes do nosso site podem se tornar inacessíveis ou não funcionar corretamente, especialmente funcionalidades que requerem login.
            </p>

            <div className="bg-muted p-4 rounded-lg mb-4">
              <p className="text-sm font-semibold mb-2">Links para gerenciar cookies nos principais navegadores:</p>
              <ul className="text-sm space-y-1">
                <li><strong>Google Chrome:</strong> <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Instruções</a></li>
                <li><strong>Mozilla Firefox:</strong> <a href="https://support.mozilla.org/pt-BR/kb/protecao-aprimorada-contra-rastreamento-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Instruções</a></li>
                <li><strong>Safari:</strong> <a href="https://support.apple.com/pt-br/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Instruções</a></li>
                <li><strong>Microsoft Edge:</strong> <a href="https://support.microsoft.com/pt-br/microsoft-edge/excluir-cookies-no-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Instruções</a></li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mb-3">5.2. Suas Escolhas</h3>
            <p className="mb-4">
              Ao visitar nosso site pela primeira vez, você verá um banner de cookies onde pode escolher:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Aceitar Todos:</strong> Permite todos os cookies, incluindo funcionais e de análise (quando disponíveis)</li>
              <li><strong>Apenas Essenciais:</strong> Permite apenas cookies estritamente necessários para o funcionamento do site</li>
            </ul>
            <p className="mb-4">
              Você pode alterar suas preferências a qualquer momento limpando os cookies do seu navegador e revisitando nosso site.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Armazenamento Local (Local Storage)</h2>
            <p className="mb-4">
              Além de cookies, também utilizamos o armazenamento local do navegador (localStorage) para armazenar informações como:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Preferências de tema (dark/light mode)</li>
              <li>Estado da aplicação (para melhorar a experiência do usuário)</li>
              <li>Tokens de autenticação (para manter você conectado)</li>
            </ul>
            <p className="mb-4">
              O armazenamento local funciona de forma similar aos cookies, mas os dados não são enviados automaticamente para nossos servidores a cada requisição. Você pode limpar o armazenamento local através das configurações do seu navegador.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Cookies e Proteção de Dados</h2>
            <p className="mb-4">
              O uso de cookies está em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018) e o Marco Civil da Internet (Lei 12.965/2014). Coletamos e processamos dados através de cookies apenas:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Com seu consentimento explícito (para cookies não essenciais)</li>
              <li>Quando necessário para o funcionamento do serviço (cookies essenciais)</li>
              <li>Para cumprir obrigações legais</li>
            </ul>
            <p className="mb-4">
              Para mais informações sobre como tratamos seus dados pessoais, consulte nossa <Link to="/privacidade" className="text-primary hover:underline">Política de Privacidade</Link>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Base Legal para Processamento (LGPD)</h2>
            <p className="mb-4">
              Conforme a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018), o processamento de dados pessoais através de cookies é realizado com base nas seguintes bases legais:
            </p>
            
            <h3 className="text-xl font-semibold mb-3">8.1. Cookies Essenciais</h3>
            <p className="mb-4">
              <strong>Base Legal:</strong> Execução de contrato e cumprimento de obrigação legal (LGPD Art. 7º, incisos V e II)
            </p>
            <p className="mb-4">
              Cookies essenciais são necessários para o cumprimento do contrato de prestação de serviços e para cumprir obrigações legais de segurança e autenticação. Estes cookies não requerem consentimento, mas informamos sobre seu uso de forma transparente.
            </p>

            <h3 className="text-xl font-semibold mb-3">8.2. Cookies Funcionais</h3>
            <p className="mb-4">
              <strong>Base Legal:</strong> Consentimento do titular (LGPD Art. 7º, inciso I) e legítimo interesse (LGPD Art. 7º, inciso IX)
            </p>
            <p className="mb-4">
              Cookies funcionais melhoram sua experiência, mas não são estritamente necessários. Solicitamos seu consentimento explícito antes de utilizá-los. Você pode retirar seu consentimento a qualquer momento.
            </p>

            <h3 className="text-xl font-semibold mb-3">8.3. Cookies de Análise</h3>
            <p className="mb-4">
              <strong>Base Legal:</strong> Consentimento do titular (LGPD Art. 7º, inciso I)
            </p>
            <p className="mb-4">
              Cookies de análise e desempenho requerem consentimento explícito. Atualmente não utilizamos cookies de análise de terceiros. Se implementarmos no futuro, solicitaremos seu consentimento específico.
            </p>

            <h3 className="text-xl font-semibold mb-3">8.4. Seus Direitos</h3>
            <p className="mb-4">
              Conforme a LGPD, você possui os seguintes direitos em relação aos dados coletados via cookies:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Confirmação e Acesso:</strong> Saber se processamos seus dados e acessar esses dados</li>
              <li><strong>Correção:</strong> Solicitar correção de dados incompletos, inexatos ou desatualizados</li>
              <li><strong>Anonimização, Bloqueio ou Eliminação:</strong> Solicitar anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos</li>
              <li><strong>Portabilidade:</strong> Solicitar portabilidade dos dados para outro fornecedor</li>
              <li><strong>Eliminação:</strong> Solicitar eliminação de dados tratados com base em consentimento</li>
              <li><strong>Revogação de Consentimento:</strong> Retirar seu consentimento a qualquer momento</li>
              <li><strong>Informação:</strong> Obter informações sobre compartilhamento de dados com terceiros</li>
            </ul>
            <p className="mb-4">
              Para exercer seus direitos, entre em contato através do e-mail <strong>atendimento.fabtech@gmail.com</strong> ou consulte nossa <Link to="/privacidade" className="text-primary hover:underline">Política de Privacidade</Link>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Atualizações desta Política</h2>
            <p className="mb-4">
              Podemos atualizar esta Política de Cookies periodicamente para refletir mudanças em nossas práticas ou por outros motivos operacionais, legais ou regulatórios.
            </p>
            <p className="mb-4">
              Quando fizermos alterações significativas, notificaremos você através de um aviso em nosso site ou por e-mail. A data da "Última atualização" no topo desta página indica quando esta política foi revisada pela última vez.
            </p>
            <p className="mb-4">
              Recomendamos que você revise esta política periodicamente para se manter informado sobre como usamos cookies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Contato</h2>
            <p className="mb-4">
              Se você tiver dúvidas sobre nossa Política de Cookies ou sobre como usamos cookies, entre em contato conosco:
            </p>
            <div className="bg-muted p-4 rounded-lg mb-4">
              <p className="mb-2"><strong>FabChat</strong></p>
              <p className="mb-2">E-mail: <strong>atendimento.fabtech@gmail.com</strong></p>
              <p className="mb-2">Suporte: <strong>atendimento.fabtech@gmail.com</strong></p>
            </div>
            <p className="mb-4">
              Para exercer seus direitos sob a LGPD (acesso, correção, exclusão de dados, etc.), consulte nossa <Link to="/privacidade" className="text-primary hover:underline">Política de Privacidade</Link>.
            </p>
          </section>

          <div className="mt-12 pt-8 border-t border-border">
            <Link 
              to="/" 
              className="text-primary hover:underline"
            >
              ← Voltar ao início
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

